import { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LogEntriesPerDateFetchStrategy,
  LogTemplateType,
  LogFieldValueInput,
  useAllWorklogsTemplatesForKpiTrackerQuery,
  useLogEntriesPerDateQuery,
  useUpsertLogEntryMutation,
  LogCategory,
  LogsTemplate,
  ShiftType,
  SectionUnitsEnum,
  TemplateFieldTypeEnum,
  GetAggregatedKpiLogEntriesDocument,
  GetLogEntryCardsDocument,
  LogEntriesByReferenceDocument,
} from '@/__generated__/graphql';
import { useGetPlatformId } from '@/hooks/useGetPlatformId';
import * as kpiState from '../state';
import { useKpiPanelStructureId } from './useKpiPanelStructureId';
import { KpiFormValues, WorklogsField } from '../types';
import type { KpiWorkLogFormContextValue } from '../types';
import {
  convertValueByUnit,
  formatDateToYYYYMMDD,
  getSectionUnitForField,
  toIndividualHours,
  toTotalPobHours,
} from '../utils';
import { KPI_POB_FIELD_KEY } from '../constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import * as globalState from '@/components/Analysis/state';
import { COLLECTION_NAMES } from '@/constants';

export const useKpiWorkLogForm = () => {
  const logType = useAtomValue(kpiState.selectedLogTemplateType);
  const structureId = useKpiPanelStructureId();
  const platformId = useGetPlatformId();
  const selectedWorkpackIdForKpiTracker = useAtomValue(kpiState.selectedWorkpackIdForKpiTracker);
  const selectedWbsFilter = useAtomValue(kpiState.selectedWbsFilter);
  const setSelectedWorklogsTemplate = useSetAtom(kpiState.selectedWorklogsTemplate);
  const setSelectedCostLogsTemplateForKpi = useSetAtom(kpiState.selectedCostLogsTemplateForKpi);
  const selectedDate = useAtomValue(kpiState.selectedDate);
  const unitSystem = useAtomValue(globalState.unitSystem);
  const selectedShift = useAtomValue(kpiState.selectedShift);
  const { showSnackBar } = useSnackBarMessage({ atom: kpiState.kpiPanelSnackbarMessage });
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedDate = formatDateToYYYYMMDD(selectedDate);

  const commonWorkpackLogsPerDateQueryInput = useMemo(
    () => ({
      timezone: userTimezone,
      logDate: formattedDate,
      structureId: logType === LogTemplateType.CostLogTemplate ? undefined : structureId,
      referenceId:
        logType === LogTemplateType.WorklogTemplate
          ? selectedWorkpackIdForKpiTracker || ''
          : platformId || '',
      collectionName:
        logType === LogTemplateType.WorklogTemplate
          ? COLLECTION_NAMES.WORKPACK
          : COLLECTION_NAMES.PLATFORM,
      category:
        logType === LogTemplateType.CostLogTemplate ? LogCategory.CostLog : LogCategory.Worklog,
      shift: logType === LogTemplateType.CostLogTemplate ? ShiftType.Day : selectedShift,
      ...(logType === LogTemplateType.CostLogTemplate &&
        selectedWbsFilter && { type: selectedWbsFilter }),
    }),
    [
      logType,
      structureId,
      selectedWorkpackIdForKpiTracker,
      platformId,
      selectedWbsFilter,
      selectedShift,
      userTimezone,
      formattedDate,
    ]
  );

  // Fetch logs template (worklog or cost log) using same query hook
  const shouldSkipTemplateQuery =
    !structureId ||
    (logType !== LogTemplateType.WorklogTemplate && logType !== LogTemplateType.CostLogTemplate);
  const templateQuery = useAllWorklogsTemplatesForKpiTrackerQuery({
    variables: shouldSkipTemplateQuery
      ? undefined
      : {
          input: {
            structureId,
            type: logType,
          },
        },
    fetchPolicy: 'cache-first',
    skip: shouldSkipTemplateQuery,
  });

  const { data: templateData, error: templateError, loading: templateLoading } = templateQuery;

  // Set appropriate template on completion
  const setSelectedTemplate =
    logType === LogTemplateType.WorklogTemplate
      ? setSelectedWorklogsTemplate
      : setSelectedCostLogsTemplateForKpi;

  // Handle template completion
  useMemo(() => {
    if (!templateData) return;

    if ('allLogsTemplates' in templateData && templateData.allLogsTemplates?.at(0)) {
      const template = templateData.allLogsTemplates.at(0) as LogsTemplate;
      setSelectedTemplate(template);
    }
  }, [templateData, setSelectedTemplate]);

  const shouldSkipLogEntriesQuery =
    (logType === LogTemplateType.WorklogTemplate &&
      (!selectedWorkpackIdForKpiTracker || !structureId)) ||
    (logType === LogTemplateType.CostLogTemplate && !platformId);

  const { data: todaysLogData, refetch: refetchTodaysLogEntries } = useLogEntriesPerDateQuery({
    variables: {
      input: {
        ...commonWorkpackLogsPerDateQueryInput,
        fetchStrategy: LogEntriesPerDateFetchStrategy.Individual,
      },
    },
    skip: shouldSkipLogEntriesQuery,
    fetchPolicy: 'cache-first',
  });

  // Aggregated logs
  const {
    data: logsData,
    loading: logsLoading,
    refetch: refetchAggregatedLogEntries,
  } = useLogEntriesPerDateQuery({
    variables: {
      input: {
        ...commonWorkpackLogsPerDateQueryInput,
        fetchStrategy: LogEntriesPerDateFetchStrategy.Aggregated,
      },
    },
    skip: shouldSkipLogEntriesQuery,
    fetchPolicy: 'network-only',
  });

  const [upsertLogEntry, { loading: isSubmitting }] = useUpsertLogEntryMutation();

  const template = (templateData as any)?.allLogsTemplates?.at(0);
  const sections = template?.sections;
  const workpackLogs = logsData?.logEntriesPerDate;
  const isQuerySkipped =
    (logType === LogTemplateType.WorklogTemplate && !structureId) ||
    (logType === LogTemplateType.CostLogTemplate && !platformId);

  // Build initial form values from template fields
  const initialValues = useMemo<KpiFormValues>(() => {
    const values: KpiFormValues = {};

    if (template?.fields) {
      const pobLog = todaysLogData?.logEntriesPerDate?.find(
        (log) => log.fieldKey === KPI_POB_FIELD_KEY
      );
      const pobValue = pobLog ? Number(pobLog.total) : 0;

      template.fields.forEach((field: WorklogsField) => {
        if (field.key) {
          const isTextareaField = field.type === TemplateFieldTypeEnum.Textarea;
          // check if today's log has data for this field
          const todaysLog = todaysLogData?.logEntriesPerDate?.find(
            (log) => log.fieldKey === field.key
          );

          if (isTextareaField) {
            values[field.key] = { value: todaysLog?.textValue ?? '' };
            return;
          }

          if (todaysLog) {
            const sectionUnit = getSectionUnitForField(field.key, sections);
            const convertedValue = convertValueByUnit(
              todaysLog.total,
              sectionUnit,
              unitSystem,
              'view',
              sectionUnit === SectionUnitsEnum.Area
                ? { viewRoundDecimals: field.roundToDecimal ?? 3 }
                : undefined
            );

            const isHoursField = sectionUnit === SectionUnitsEnum.Hours;

            values[field.key] = {
              value: isHoursField ? toIndividualHours(convertedValue, pobValue) : convertedValue,
            };
          } else {
            values[field.key] = { value: '' };
          }
        }
      });
    }

    return values;
  }, [template?.fields, todaysLogData?.logEntriesPerDate, sections, unitSystem]);

  // Build validation schema dynamically from template fields
  const validationSchema = useMemo(() => {
    if (!template?.fields) return Yup.object({}) as any;

    const schemaFields = template.fields.reduce(
      (acc: Record<string, Yup.Schema>, field: WorklogsField) => {
        if (field.key) {
          if (field.type === TemplateFieldTypeEnum.Textarea) {
            acc[field.key] = Yup.object({
              value: Yup.mixed().test('is-string', 'Must be text', (value) => {
                if (value === '' || value === undefined || value === null) return true;
                return typeof value === 'string';
              }),
            });
            return acc;
          }

          acc[field.key] = Yup.object({
            value: Yup.mixed()
              .test('is-valid-number', 'Must be a valid number', (value) => {
                if (value === '' || value === undefined || value === null) return true;
                return typeof value === 'number' && !Number.isNaN(value);
              })
              .test('is-non-negative', 'Value must be 0 or greater', (value) => {
                if (value === '' || value === undefined || value === null) return true;
                return typeof value === 'number' && value >= 0;
              }),
          });
        }
        return acc;
      },
      {}
    );

    // Add POB validation
    schemaFields.pob = Yup.object({
      value: Yup.mixed()
        .test('is-valid-number', 'Must be a valid number', (value) => {
          if (value === '' || value === undefined || value === null) return true;
          return typeof value === 'number' && !Number.isNaN(value);
        })
        .test('is-non-negative', 'Value must be 0 or greater', (value) => {
          if (value === '' || value === undefined || value === null) return true;
          return typeof value === 'number' && value >= 0;
        }),
    });

    return Yup.object(schemaFields);
  }, [template?.fields]);

  // Build field values from form values for upsertLogEntry
  const buildFieldValues = (values: KpiFormValues): LogFieldValueInput[] => {
    const pobValue = Number(values[KPI_POB_FIELD_KEY]?.value) || 0;
    const fieldsByKey = new Map<string, WorklogsField>(
      (template?.fields ?? [])
        .filter((field: WorklogsField) => Boolean(field.key))
        .map((field: WorklogsField) => [field.key as string, field])
    );

    return Object.entries(values).map(([fieldKey, fieldValue]) => {
      const field = fieldsByKey.get(fieldKey);
      const isTextareaField = field?.type === TemplateFieldTypeEnum.Textarea;

      if (isTextareaField) {
        const textContent = typeof fieldValue.value === 'string' ? fieldValue.value : '';
        if (textContent === '') {
          return { fieldKey, value: 0, delete: true };
        }
        return { fieldKey, value: textContent };
      }

      if (fieldValue.value === '') {
        return { fieldKey, value: 0, delete: true };
      }

      const sectionUnit = getSectionUnitForField(fieldKey, sections);
      const isHoursField = sectionUnit === SectionUnitsEnum.Hours;

      const convertedValue = convertValueByUnit(
        fieldValue.value as number,
        sectionUnit,
        unitSystem,
        'update'
      );

      return {
        fieldKey,
        value: isHoursField ? toTotalPobHours(convertedValue, pobValue) : convertedValue,
      };
    });
  };

  const formik = useFormik<KpiFormValues>({
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!template?.id) return;

      const referenceId =
        logType === LogTemplateType.WorklogTemplate ? selectedWorkpackIdForKpiTracker : platformId;
      const queryStructureId = structureId;

      if (!referenceId) return;
      if (!queryStructureId) return;

      const fieldValues = buildFieldValues(values);
      const allDeleted = fieldValues.length > 0 && fieldValues.every((fv) => fv.delete);

      try {
        const input = {
          logDate: formattedDate,
          structureId: queryStructureId,
          referenceId,
          collectionName:
            logType === LogTemplateType.WorklogTemplate
              ? COLLECTION_NAMES.WORKPACK
              : COLLECTION_NAMES.PLATFORM,
          category:
            logType === LogTemplateType.CostLogTemplate ? LogCategory.CostLog : LogCategory.Worklog,
          logsTemplateId: template.id,
          shift: logType === LogTemplateType.CostLogTemplate ? ShiftType.Day : selectedShift,
          fieldValues,
          ...(logType === LogTemplateType.CostLogTemplate &&
            selectedWbsFilter && { type: selectedWbsFilter }),
          lastModifiedFor: allDeleted ? 'Delete Work Log' : 'Update Work Log',
        };

        await upsertLogEntry({
          variables: {
            input,
          },
          refetchQueries: [
            GetAggregatedKpiLogEntriesDocument,
            GetLogEntryCardsDocument,
            LogEntriesByReferenceDocument,
          ],
          awaitRefetchQueries: true,
        });

        if (!shouldSkipLogEntriesQuery) {
          await Promise.all([
            refetchTodaysLogEntries({
              input: {
                ...commonWorkpackLogsPerDateQueryInput,
                fetchStrategy: LogEntriesPerDateFetchStrategy.Individual,
              },
            }),
            refetchAggregatedLogEntries({
              input: {
                ...commonWorkpackLogsPerDateQueryInput,
                fetchStrategy: LogEntriesPerDateFetchStrategy.Aggregated,
              },
            }),
          ]);
        }

        const successMessage =
          logType === LogTemplateType.CostLogTemplate
            ? 'Cost log saved successfully'
            : 'Work log saved successfully';
        showSnackBar(successMessage, 'success');
      } catch (error) {
        const message = extractErrorMessageFromUnknownError(error);
        showSnackBar(message, 'error');
      }
    },
  });

  const value = useMemo<KpiWorkLogFormContextValue>(
    () => ({
      formik,
      isSubmitting,
      template,
      sections,
      workpackLogs,
      logsLoading,
      isLoading: templateLoading || isQuerySkipped,
      error: templateError,
    }),
    [
      formik,
      isSubmitting,
      template,
      sections,
      workpackLogs,
      logsLoading,
      templateLoading,
      isQuerySkipped,
      templateError,
    ]
  );

  const setFormContext = useSetAtom(kpiState.kpiWorkLogFormContextAtom);
  useEffect(() => {
    setFormContext(value);
  }, [setFormContext, value]);

  return value;
};

/**
 * Hook for child components to access the shared form context.
 * Must be used within a component tree where useKpiWorkLogForm has been called.
 */
export const useKpiWorkLogFormContext = (): KpiWorkLogFormContextValue => {
  const context = useAtomValue(kpiState.kpiWorkLogFormContextAtom);

  if (!context) {
    throw new Error(
      'useKpiWorkLogFormContext must be used after useKpiWorkLogForm has been called'
    );
  }

  return context;
};
